import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/auth/authService';

/**
 * How long before the deadline the warning appears — always five minutes, whatever the
 * tenant's idle window is. An idle window at or below five minutes therefore has the
 * countdown running from the start of inactivity; that is the accepted trade-off, since
 * such windows only exist for verification rounds.
 */
const WARN_BEFORE_MS = 5 * 60 * 1000;

/** One tick per second — the countdown is the point, so it has to move. */
const TICK_MS = 1000;

const formatRemaining = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Counts down the last five minutes of a session and offers to keep it alive.
 *
 * The offer is only made when it can be honoured. An idle deadline moves when the user
 * acts, so "Stay signed in" genuinely postpones it — by making one real refresh, which is
 * what the server reads as activity. The absolute bound moves for nobody, so against that
 * one the dialog says the session is ending and shows no button that pretends otherwise.
 *
 * Expiry itself is still the server's call: this only decides when the user is told.
 */
export const SessionExpiryWarning = () => {
  const { t } = useTranslation();
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [canExtend, setCanExtend] = useState(true);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    const tick = () => {
      const deadline = authService.getSessionDeadline();
      if (!deadline) {
        setRemainingMs(null);
        return;
      }

      const remaining = deadline.expiresAt - Date.now();
      if (remaining <= 0) {
        // End it here. Do NOT refresh first to "confirm": that round trip moves the
        // server's activity anchor, which moves this deadline, which renews the session
        // the timer just decided was over — measured as an endless token/policy loop.
        // Other tabs are covered by the activity broadcast instead, so a tab that reaches
        // zero really has seen nothing happen anywhere.
        setRemainingMs(null);
        authService.notifySessionEnded();
        return;
      }

      setCanExtend(deadline.canExtend);
      setRemainingMs(remaining <= WARN_BEFORE_MS ? remaining : null);
    };

    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const staySignedIn = async () => {
    setExtending(true);
    try {
      const extended = await authService.extendSession();
      if (!extended) {
        authService.notifySessionEnded();
        return;
      }
      setRemainingMs(null);
    } finally {
      setExtending(false);
    }
  };

  const open = remainingMs !== null;

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        {canExtend
          ? t('session.warningTitle', { defaultValue: 'You are about to be signed out' })
          : t('session.maxLengthTitle', { defaultValue: 'Your session is ending' })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {canExtend
            ? t('session.warningMessage', {
                remaining: formatRemaining(remainingMs ?? 0),
                defaultValue:
                  'You have been inactive. You will be signed out in {{remaining}}. Save anything you are working on, or stay signed in.',
              })
            : t('session.maxLengthMessage', {
                remaining: formatRemaining(remainingMs ?? 0),
                defaultValue:
                  'This session reaches its maximum length in {{remaining}} and cannot be extended. Save your work and sign in again.',
              })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {canExtend && (
          <Button onClick={staySignedIn} variant="contained" color="primary" disabled={extending} autoFocus>
            {t('session.staySignedIn', { defaultValue: 'Stay signed in' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
